/**
 * Deterministic, seedable random number generation.
 *
 * Every source of randomness in the app goes through this, so that a composition
 * is exactly reproducible from (seed + parameters) alone — which is what makes the
 * batch mode, the share links and the PNG metadata round-trip meaningful.
 *
 * Generator: mulberry32 (Tommy Ettinger) — 32-bit state, passes gjrand, very fast.
 */

export interface Rng {
  /** Uniform in [0, 1). */
  next(): number;
  /** Uniform in [min, max). */
  range(min: number, max: number): number;
  /** Integer in [min, max] inclusive. */
  int(min: number, max: number): number;
  /** True with probability p. */
  chance(p: number): boolean;
  /** Uniform in [-a, a). */
  signed(a: number): number;
  /** Normal distribution via Box-Muller. */
  gaussian(mean?: number, sigma?: number): number;
  /** Exponential distribution with the given rate (lambda). */
  exponential(lambda?: number): number;
  pick<T>(items: readonly T[]): T;
  /** Weighted pick; weights need not be normalised. */
  pickWeighted<T>(items: readonly T[], weights: readonly number[]): T;
  /** Fisher-Yates, returns a new array. */
  shuffle<T>(items: readonly T[]): T[];
  /** A point uniformly distributed inside the unit disc. */
  insideDisc(): { x: number; y: number };
  /** A fresh independent generator, itself deterministic. Used to give layers their own streams. */
  fork(salt?: number): Rng;
  readonly seed: number;
}

/** Hashes an arbitrary string into a 32-bit integer seed (xmur3). */
export function hashSeed(str: string): number {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  h = Math.imul(h ^ (h >>> 16), 2246822507);
  h = Math.imul(h ^ (h >>> 13), 3266489909);
  return (h ^= h >>> 16) >>> 0;
}

export function createRng(seed: number): Rng {
  let state = (seed >>> 0) || 1;

  const next = (): number => {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  // Box-Muller produces two normals at a time; cache the spare.
  let spare: number | null = null;

  const rng: Rng = {
    seed: seed >>> 0,
    next,
    range: (min, max) => min + next() * (max - min),
    int: (min, max) => min + Math.floor(next() * (max - min + 1)),
    chance: (p) => next() < p,
    signed: (a) => (next() * 2 - 1) * a,
    gaussian(mean = 0, sigma = 1) {
      if (spare !== null) {
        const v = spare;
        spare = null;
        return mean + sigma * v;
      }
      let u = 0;
      let v = 0;
      let s = 0;
      do {
        u = next() * 2 - 1;
        v = next() * 2 - 1;
        s = u * u + v * v;
      } while (s >= 1 || s === 0);
      const mul = Math.sqrt((-2 * Math.log(s)) / s);
      spare = v * mul;
      return mean + sigma * u * mul;
    },
    exponential: (lambda = 1) => -Math.log(1 - next()) / lambda,
    pick: (items) => items[Math.floor(next() * items.length)],
    pickWeighted(items, weights) {
      let total = 0;
      for (let i = 0; i < items.length; i++) total += weights[i] ?? 0;
      let r = next() * total;
      for (let i = 0; i < items.length; i++) {
        r -= weights[i] ?? 0;
        if (r <= 0) return items[i];
      }
      return items[items.length - 1];
    },
    shuffle(items) {
      const out = items.slice();
      for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(next() * (i + 1));
        const tmp = out[i];
        out[i] = out[j];
        out[j] = tmp;
      }
      return out;
    },
    insideDisc() {
      // Rejection sampling: uniform over the disc without the sqrt bias of (r, theta) sampling.
      for (;;) {
        const x = next() * 2 - 1;
        const y = next() * 2 - 1;
        if (x * x + y * y <= 1) return { x, y };
      }
    },
    fork(salt = 0) {
      return createRng(hashSeed(`${seed}:${salt}:${Math.floor(next() * 0xffffffff)}`));
    },
  };

  return rng;
}

/** A seed suitable for showing in the UI: 9 digits, easy to read out loud and retype. */
export function randomSeed(): number {
  return Math.floor(Math.random() * 999999999);
}
